import { useMemo } from 'react';

import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import ReactMarkdown from 'react-markdown';

import { generateUrl, getPluginForTable } from 'shared/base';
import { EmptyBox } from 'shared/base/components/display/emptyBox';
import { paths } from 'shared/base/constants/paths';
import { useBaseAuthenticated } from 'shared/base/contexts/baseAuth';

const missingTableRegex = /Unknown table expression identifier '(.*?)'/;

const MissingTableError = ({ markdown }: { markdown: string }) => {
  return (
    <ReactMarkdown
      components={{
        a: ({ children, href }) => (
          <Link href={href} target="_blank">
            {children}
          </Link>
        ),
      }}
    >
      {markdown}
    </ReactMarkdown>
  );
};

export const ReportCardError = ({
  clearFilters,
  error = { data: { message: '' }, status: 500 },
}: {
  clearFilters?: () => void;
  error: { data: { message: string }; status: number };
}) => {
  const { currentMembership } = useBaseAuthenticated();
  const errorProps = useMemo(() => {
    switch (error.status) {
      case 403: {
        return {
          message: 'Contact your workspace admin to resolve the issue.',
          title: 'You have no permissions to view this data',
        };
      }
      case 404: {
        return clearFilters
          ? {
              action: (
                <Button color="secondary" onClick={clearFilters} variant="outlined">
                  Enable default custom view
                </Button>
              ),
              message: 'Data in the selected view isn’t sufficient for this visualization. ',
              title: 'No data found with your custom view',
            }
          : {
              message:
                'Make sure your integrations are enabled and tables required for this visualization are synced. ',
              title: 'No data found',
            };
      }
      case 400: {
        const { data: { message } = { message: '' } } = error;
        const missingTable = message.match(missingTableRegex);
        if (missingTable) {
          const plugin = getPluginForTable(missingTable[1]);
          const createIntegrationLink = generateUrl({
            pathname: paths.platform.team.syncs.sources.create,
            query: {
              plugin,
              tables: missingTable[1],
              team: currentMembership.team.name,
            },
          });
          const markdown = `The \`${missingTable[1]}\` table is missing.\nYou may need to [create an integration](${createIntegrationLink}) to sync the table.`;

          return {
            message: <MissingTableError markdown={markdown} />,
            title: 'Table not found',
          };
        }
        // fall through
      }
      default: {
        return {
          message: `Error: ${error.data.message}`,
          title: 'Something went wrong',
        };
      }
    }
  }, [error, clearFilters, currentMembership]);

  return <EmptyBox {...errorProps} />;
};
